import { defineComponent, onMounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import { Col as VanCol, Icon as VanIcon, NavBar as VanNavBar, Row as VanRow } from 'vant'
import { getApiV1FansRanking } from '@/api/sdk.gen'
import type { FansRankingItem } from '@/api/types.gen'
import { useAuthStore } from '@/stores/auth-store'
import UserAvatar from '@/components/user-avatar'

// 三甲奖杯角标
const TOP_BADGES: Record<1 | 2 | 3, string> = {
  1: '/assets/49208/img/top1.png',
  2: '/assets/49208/img/top2.png',
  3: '/assets/49208/img/top3.png',
}

// 三甲头像 + 角标（top=1/2/3）
const UserImgTop = defineComponent({
  props: {
    top: { type: Number as () => 1 | 2 | 3, required: true },
    src: { type: String, default: '' },
    userId: { type: Number, default: 0 },
  },
  setup(props) {
    return () => (
      props.userId ? (
        <RouterLink class={['user-img-top btn-reset', `top${props.top}`]} to={`/user/${props.userId}`}>
          <UserAvatar class="userimg" src={props.src} />
          <img class="userimgxs" src={TOP_BADGES[props.top]} alt="" />
        </RouterLink>
      ) : (
        <div class={['user-img-top', `top${props.top}`]}>
          <UserAvatar class="userimg" src={props.src} />
          <img class="userimgxs" src={TOP_BADGES[props.top]} alt="" />
        </div>
      )
    )
  },
})

export default defineComponent({
  name: 'TopFansRankPage',
  setup() {
    const router = useRouter()
    const goBack = useSmartBack()
    const authStore = useAuthStore()

    const list = ref<FansRankingItem[]>([])
    const self = ref<FansRankingItem | null>(null)

    async function load() {
      try {
        const res = await getApiV1FansRanking()
        list.value = res.data?.data?.list ?? []
        self.value = res.data?.data?.self ?? null
      } catch {
        list.value = []
        self.value = null
      }
    }

    onMounted(load)

    return () => {
      const top1 = list.value[0]
      const top2 = list.value[1]
      const top3 = list.value[2]
      const rest = list.value.slice(3)
      const showTopHead = list.value.length > 0
      // 登录后才显示「我的排名」（依据 self 字段）
      const showMyTop = authStore.isLoggedIn && !!self.value

      return (
        <div class="top-grade">
          <VanNavBar title="粉丝排行榜" border={false} onClickLeft={() => goBack()}>
            {{
              left: () => <VanIcon name="arrow-left" color="#fff" size="16" />,
            }}
          </VanNavBar>

          {showTopHead && (
            <div class="top-head">
              <div class="top-hd">
                {/* 第 2 名 */}
                <div class="top-2">
                  {top2 && (
                    <UserImgTop top={2} src={top2.avatar ?? ''} userId={top2.user_id ?? 0} />
                  )}
                  <div class="name van-ellipsis">{top2?.nickname ?? '--'}</div>
                  <div class="ms">{top2?.fans_count ?? 0}粉丝</div>
                </div>
                {/* 第 1 名 */}
                <div class="top-1">
                  {top1 && (
                    <UserImgTop top={1} src={top1.avatar ?? ''} userId={top1.user_id ?? 0} />
                  )}
                  <div class="name van-ellipsis">{top1?.nickname ?? '--'}</div>
                  <div class="ms">{top1?.fans_count ?? 0}粉丝</div>
                </div>
                {/* 第 3 名 */}
                <div class="top-3">
                  {top3 && (
                    <UserImgTop top={3} src={top3.avatar ?? ''} userId={top3.user_id ?? 0} />
                  )}
                  <div class="name van-ellipsis">{top3?.nickname ?? '--'}</div>
                  <div class="ms">{top3?.fans_count ?? 0}粉丝</div>
                </div>
              </div>

              {showMyTop && (
                <div class="my-top">
                  <VanRow class="top-item">
                    <VanCol class="czz" span={18}>
                      <div class="user-top">
                        <UserAvatar class="userimg" src={self.value?.avatar ?? ''} />
                        <div class="user-text">
                          <div class="name">我</div>
                          <div class="pm">
                            {self.value?.rank ? `排名第${self.value.rank}` : '暂无排名'}
                          </div>
                        </div>
                      </div>
                    </VanCol>
                    <VanCol class="leve-n" span={6}>
                      {self.value?.fans_count ?? 0}粉丝
                    </VanCol>
                  </VanRow>
                </div>
              )}
            </div>
          )}

          {rest.length > 0 && (
            <div class="top-body-list">
              <div class="top-list">
                {rest.map((u) => (
                  <VanRow key={u.user_id} class="top-item">
                    <VanCol class="num" span={3}>
                      {u.rank}
                    </VanCol>
                    <VanCol class="czz" span={15}>
                      <div class="user-top">
                        <UserAvatar
                          class="userimg"
                          src={u.avatar ?? ''}
                          onClick={() => u.user_id && router.push(`/user/${u.user_id}`)}
                        />
                        <div class="user-text">
                          <div class="name p-top van-ellipsis">{u.nickname ?? '--'}</div>
                        </div>
                      </div>
                    </VanCol>
                    <VanCol class="leve-n" span={6}>
                      {u.fans_count ?? 0}粉丝
                    </VanCol>
                  </VanRow>
                ))}
              </div>
            </div>
          )}
        </div>
      )
    }
  },
})
